import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { 
  Droplets, 
  Home, 
  Sparkles, 
  Shield, 
  Clock, 
  Phone, 
  Mail, 
  MapPin,
  ChevronRight,
  CheckCircle2,
  Star,
  Menu,
  X,
  Send,
  Waves,
  Award,
  Users,
  ThumbsUp
} from "lucide-react";
import { Toaster, toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Services', href: '#services' },
    { name: 'Why Us', href: '#why-us' },
    { name: 'Contact', href: '#contact' },
  ];

  return (
    <nav 
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#0a1628]/95 backdrop-blur-lg shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a 
            href="#" 
            data-testid="logo"
            className="flex items-center group"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_web-launch-40/artifacts/2yysdss0_C4DDAE10-AA72-454E-BFCF-D9C280CEC90F.PNG" 
              alt="Crystal Clear Home Services Logo"
              className="w-12 h-12 object-contain -mr-1"
            />
            <span className="text-xl font-bold text-white font-['Outfit']">
              Crystal Clear<span className="text-cyan-400"> Home Services</span>
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="items-center gap-8 hidden md:flex">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                className="text-slate-300 hover:text-cyan-400 font-medium transition-colors relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-cyan-400 after:transition-all hover:after:w-full"
              >
                {link.name}
              </a>
            ))}
            <a
              href="#contact"
              data-testid="nav-cta-button"
              className="bg-orange-500 hover:bg-orange-600 text-white font-medium px-6 py-2.5 rounded-full transition-all duration-300 hover:shadow-lg hover:shadow-orange-500/25 hover:-translate-y-0.5 btn-ripple"
            >
              Get Free Quote
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            className="p-2 text-white hover:text-cyan-400 transition-colors md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div 
            data-testid="mobile-menu"
            className="md:hidden bg-[#0a1628]/95 backdrop-blur-lg rounded-2xl mt-2 p-4 animate-fade-in border border-white/10"
          >
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
                className="block py-3 px-4 text-slate-300 hover:text-cyan-400 hover:bg-white/5 rounded-xl font-medium transition-all"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </a>
            ))}
            <a
              href="#contact"
              data-testid="mobile-nav-cta"
              className="block mt-2 bg-orange-500 hover:bg-orange-600 text-white font-medium px-6 py-3 rounded-full text-center transition-all"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Get Free Quote
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background photo with overlay */}
      <div className="absolute inset-0">
        <img 
          src="https://customer-assets.emergentagent.com/job_web-launch-40/artifacts/wonq8sd3_IMG_9469.JPG"
          alt="Beautiful home exterior"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0a1628]/70" />
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-32 pb-20 relative z-10 w-full">
        <div className="text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-orange-500/20 text-orange-400 px-4 py-2 rounded-full text-sm font-medium mb-6 animate-fade-in-up border border-orange-500/30">
            <Sparkles className="w-4 h-4" />
            Premium Exterior Cleaning Services
          </div>
          
          <h1 
            data-testid="hero-title"
            className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6 font-['Outfit'] animate-fade-in-up stagger-1"
          >
            Make Your Home
            <span className="block text-cyan-400">Shine Again</span>
          </h1>
          
          <p 
            data-testid="hero-subtitle"
            className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8 max-w-2xl mx-auto animate-fade-in-up stagger-2"
          >
            Professional pressure washing and exterior cleaning that transforms your property. 
            We bring the sparkle back to your home, driveway, and outdoor spaces.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up stagger-3">
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-4 rounded-full shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 transition-all transform hover:scale-105 btn-ripple"
            >
              Get Your Free Quote
              <ChevronRight className="w-5 h-5" />
            </a>
            <a
              href="tel:+16043758637"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 font-medium px-8 py-4 rounded-full transition-all"
            >
              <Phone className="w-5 h-5" />
              Call Now
            </a>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap gap-6 mt-10 justify-center animate-fade-in-up stagger-4">
            <div className="flex items-center gap-2 text-slate-300">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              <span className="text-sm font-medium">100% Satisfaction</span>
            </div>
            <div className="flex items-center gap-2 text-slate-300">
              <Shield className="w-5 h-5 text-cyan-400" />
              <span className="text-sm font-medium">Fully Insured</span>
            </div>
            <div className="flex items-center gap-2 text-slate-300">
              <Star className="w-5 h-5 text-yellow-400" />
              <span className="text-sm font-medium">5-Star Rated</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Home,
      title: "Soft Side Wash",
      description: "Gentle exterior cleaning to remove dirt, mold, mildew, and grime from your home's siding.",
      image: "https://customer-assets.emergentagent.com/job_web-launch-40/artifacts/2ldafqbk_IMG_9463.JPG",
      featured: true
    },
    {
      icon: Waves,
      title: "Pressure Cleaning",
      description: "High-pressure cleaning for driveways, walkways, and patios to restore their original look.",
      image: "https://customer-assets.emergentagent.com/job_web-launch-40/artifacts/78sadjtf_IMG_9464.jpg"
    },
    {
      icon: Sparkles,
      title: "Roof Cleaning",
      description: "Professional moss removal by scraping with wire brush - thorough cleaning without water damage.",
      image: "https://customer-assets.emergentagent.com/job_web-launch-40/artifacts/vefmoisx_1365A360-B5BB-4A59-B5E9-1A9039D1D9E8.jpg"
    },
    {
      icon: Droplets,
      title: "Glass & Metal Awnings",
      description: "Expert cleaning for glass and metal awnings to keep them sparkling and well-maintained.",
      image: "https://customer-assets.emergentagent.com/job_web-launch-40/artifacts/qi9h386n_IMG_9466.jpg"
    }
  ];

  return (
    <section 
      id="services" 
      data-testid="services-section"
      className="py-20 md:py-28 bg-white relative"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-sky-100 text-sky-600 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Droplets className="w-4 h-4" />
            Our Services
          </div>
          <h2 
            data-testid="services-title"
            className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 font-['Outfit']"
          >
            Professional Cleaning Solutions
          </h2>
          <p className="text-lg text-slate-600">
            From house washing to driveway cleaning, we offer comprehensive exterior cleaning services 
            to keep your property looking its best.
          </p>
        </div>

        {/* Services Grid - Bento Layout */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <div
              key={service.title}
              data-testid={`service-card-${index}`}
              className={`service-card glass rounded-2xl overflow-hidden relative group ${
                index === 0 ? 'lg:col-span-2 lg:row-span-2' : ''
              }`}
            >
              <div className={`relative ${index === 0 ? 'h-full min-h-[400px]' : 'h-64'}`}>
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent" />
                
                <div className="absolute inset-0 p-6 flex flex-col justify-end">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-sky-500 rounded-xl flex items-center justify-center">
                      <service.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white font-['Outfit']">
                      {service.title}
                    </h3>
                  </div>
                  <p className="text-white/80 text-sm md:text-base max-w-md">
                    {service.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUsSection = () => {
  const benefits = [
    {
      icon: Award,
      title: "Licensed & Insured",
      description: "Fully licensed professionals with comprehensive insurance coverage for your peace of mind."
    },
    {
      icon: Clock,
      title: "Fast & Reliable",
      description: "Quick response times and scheduled appointments that fit your busy lifestyle."
    },
    {
      icon: Shield,
      title: "Eco-Friendly",
      description: "We use environmentally safe cleaning solutions that are tough on dirt but gentle on nature."
    },
    {
      icon: Users,
      title: "Experienced Team",
      description: "Our trained technicians have years of experience in exterior cleaning services."
    }
  ];

  return (
    <section 
      id="why-us" 
      data-testid="why-us-section"
      className="py-20 md:py-28 bg-gradient-to-b from-white to-sky-50 relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute top-1/2 right-0 w-[500px] h-[500px] bg-sky-100 rounded-full blur-3xl opacity-50 transform translate-x-1/2 -translate-y-1/2" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <div className="inline-flex items-center gap-2 bg-sky-100 text-sky-600 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Shield className="w-4 h-4" />
              Why Choose Us
            </div>
            <h2 
              data-testid="why-us-title"
              className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 font-['Outfit']"
            >
              The <span className="gradient-text">Crystal Clear</span> Difference
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              We're not just another cleaning company. We're your partners in maintaining a beautiful, 
              well-cared-for property that you can be proud of.
            </p>

            <div className="grid sm:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div 
                  key={benefit.title}
                  data-testid={`benefit-card-${index}`}
                  className="group"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-sky-100 group-hover:bg-sky-500 rounded-xl flex items-center justify-center transition-colors duration-300 flex-shrink-0">
                      <benefit.icon className="w-6 h-6 text-sky-500 group-hover:text-white transition-colors duration-300" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1 font-['Outfit']">
                        {benefit.title}
                      </h3>
                      <p className="text-sm text-slate-600">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Image */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-sky-50">
              <img
                src="https://customer-assets.emergentagent.com/job_web-launch-40/artifacts/4uu9bbil_crystak.jpeg"
                alt="Crystal Clear Home Services LTD worker on ladder cleaning gutters"
                data-testid="why-us-image"
                className="w-full h-[500px] object-cover object-right"
              />
            </div>
            
            {/* Stats floating card */}
            <div className="absolute -bottom-6 -right-6 glass-strong p-6 rounded-2xl shadow-xl">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-sky-500">5+</p>
                  <p className="text-sm text-slate-600">Years Experience</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-sky-500">100%</p>
                  <p className="text-sm text-slate-600">Satisfaction</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "Sarah M.",
      location: "Homeowner",
      text: "Crystal Clear transformed our home's exterior. The team was professional, on time, and the results were amazing. Our house looks brand new!",
      rating: 5
    },
    {
      name: "Michael T.",
      location: "Property Manager",
      text: "We've used Crystal Clear for multiple properties. Their consistency and quality of work is unmatched. Highly recommend for any exterior cleaning needs.",
      rating: 5
    },
    {
      name: "Jennifer L.",
      location: "Homeowner",
      text: "The driveway and patio cleaning exceeded my expectations. The pressure washing removed years of stains. Will definitely use them again!",
      rating: 5
    }
  ];

  return (
    <section 
      id="testimonials" 
      data-testid="testimonials-section"
      className="py-20 md:py-28 bg-slate-900 relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-sky-500 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-sky-400 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-sky-500/20 text-sky-400 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Star className="w-4 h-4" />
            Customer Reviews
          </div>
          <h2 
            data-testid="testimonials-title"
            className="text-4xl md:text-5xl font-bold text-white mb-4 font-['Outfit']"
          >
            What Our Customers Say
          </h2>
          <p className="text-lg text-slate-400">
            Don't just take our word for it. Here's what our satisfied customers have to say about our services.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              data-testid={`testimonial-card-${index}`}
              className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-2xl hover:bg-white/10 transition-colors duration-300"
            >
              {/* Rating */}
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              
              <p className="text-slate-300 mb-6 leading-relaxed">
                "{testimonial.text}"
              </p>
              
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-sky-400 to-sky-600 rounded-full flex items-center justify-center text-white font-bold">
                  {testimonial.name.charAt(0)}
                </div>
                <div>
                  <p className="font-semibold text-white">{testimonial.name}</p>
                  <p className="text-sm text-slate-400">{testimonial.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Form Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const services = [
    'Roof Cleaning',
    'Gutter Cleaning',
    'Gutter Guards',
    'Soft Side Wash',
    'Pressure Wash',
    'Window Clean'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Thank you! We will contact you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        service: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="contact" 
      data-testid="contact-section"
      className="py-20 md:py-28 bg-gradient-to-b from-sky-50 to-white relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-sky-100 rounded-full blur-3xl opacity-50 transform -translate-x-1/2 -translate-y-1/2" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left Content */}
          <div>
            <div className="inline-flex items-center gap-2 bg-sky-100 text-sky-600 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Mail className="w-4 h-4" />
              Get In Touch
            </div>
            <h2 
              data-testid="contact-title"
              className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 font-['Outfit']"
            >
              Request Your <span className="gradient-text">Free Quote</span>
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Ready to transform your property? Fill out the form and we'll get back to you with 
              a free, no-obligation quote within 24 hours.
            </p>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-sky-100 rounded-2xl flex items-center justify-center">
                  <Phone className="w-6 h-6 text-sky-500" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Call Us</p>
                  <a 
                    href="tel:+16043758637" 
                    data-testid="contact-phone"
                    className="text-lg font-semibold text-slate-900 hover:text-sky-500 transition-colors"
                  >
                    604-375-8637
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-sky-100 rounded-2xl flex items-center justify-center">
                  <Mail className="w-6 h-6 text-sky-500" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email Us</p>
                  <a 
                    href="mailto:CONNECT@CRYSTALCLEAR.HELP" 
                    data-testid="contact-email"
                    className="text-lg font-semibold text-slate-900 hover:text-sky-500 transition-colors"
                  >
                    CONNECT@CRYSTALCLEAR.HELP
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-sky-100 rounded-2xl flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-sky-500" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Service Area</p>
                  <p 
                    data-testid="contact-location"
                    className="text-lg font-semibold text-slate-900"
                  >
                    Metro Vancouver
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div>
            {isSubmitted ? (
              <div 
                data-testid="contact-success"
                className="glass-strong rounded-3xl p-8 text-center"
              >
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-green-500" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2 font-['Outfit']">
                  Thank You!
                </h3>
                <p className="text-slate-600 mb-6">
                  We've received your enquiry and will get back to you within 24 hours.
                </p>
                <button
                  data-testid="send-another-btn"
                  onClick={() => setIsSubmitted(false)}
                  className="text-sky-500 hover:text-sky-600 font-medium transition-colors"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form 
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="glass-strong rounded-3xl p-8"
              >
                <div className="space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      data-testid="contact-name-input"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-200 transition-all outline-none bg-white/50"
                      placeholder="John Doe"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        data-testid="contact-email-input"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-200 transition-all outline-none bg-white/50"
                        placeholder="john@example.com"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        data-testid="contact-phone-input"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-200 transition-all outline-none bg-white/50"
                        placeholder="604-375-8637"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-slate-700 mb-2">
                      Service Interested In
                    </label>
                    <select
                      id="service"
                      name="service"
                      data-testid="contact-service-select"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-200 transition-all outline-none bg-white appearance-none"
                      style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center", backgroundSize: "20px" }}
                    >
                      <option value="">Select a service</option>
                      <option value="Roof Cleaning">Roof Cleaning</option>
                      <option value="Gutter Cleaning">Gutter Cleaning</option>
                      <option value="Gutter Guards">Gutter Guards</option>
                      <option value="Soft Side Wash">Soft Side Wash</option>
                      <option value="Pressure Wash">Pressure Wash</option>
                      <option value="Window Clean">Window Clean</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="address" className="block text-sm font-medium text-slate-700 mb-2">
                      Address *
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      data-testid="contact-address-input"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-200 transition-all outline-none bg-white/50"
                      placeholder="Your address"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      data-testid="contact-message-input"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-200 transition-all outline-none bg-white/50 resize-none"
                      placeholder="Tell us what you need..."
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={isSubmitting}
                    className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-400 text-white font-bold px-8 py-4 rounded-full shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 transition-all transform hover:scale-[1.02] disabled:transform-none flex items-center justify-center gap-2 btn-ripple"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner"></span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Get Free Quote
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer 
      data-testid="footer"
      className="bg-slate-900 text-white py-16"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_web-launch-40/artifacts/2yysdss0_C4DDAE10-AA72-454E-BFCF-D9C280CEC90F.PNG" 
                alt="Crystal Clear Home Services Logo"
                className="w-12 h-12 object-contain -mr-1"
              />
              <span className="text-xl font-bold font-['Outfit']">
                Crystal Clear<span className="text-sky-400"> Home Services</span>
              </span>
            </div>
            <p className="text-slate-400 max-w-sm mb-6">
              Professional exterior cleaning services that transform your property. 
              Crystal Clear Home Services brings the sparkle back to your home.
            </p>
            <div className="flex gap-4">
              <a 
                href="#" 
                data-testid="social-facebook"
                className="w-10 h-10 bg-slate-800 hover:bg-sky-500 rounded-full flex items-center justify-center transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z"/>
                </svg>
              </a>
              <a 
                href="#" 
                data-testid="social-instagram"
                className="w-10 h-10 bg-slate-800 hover:bg-sky-500 rounded-full flex items-center justify-center transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold mb-4 font-['Outfit']">Quick Links</h3>
            <ul className="space-y-3">
              <li><a href="#services" className="text-slate-400 hover:text-sky-400 transition-colors">Services</a></li>
              <li><a href="#why-us" className="text-slate-400 hover:text-sky-400 transition-colors">Why Choose Us</a></li>
              <li><a href="#testimonials" className="text-slate-400 hover:text-sky-400 transition-colors">Testimonials</a></li>
              <li><a href="#contact" className="text-slate-400 hover:text-sky-400 transition-colors">Contact</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-semibold mb-4 font-['Outfit']">Contact Us</h3>
            <ul className="space-y-3 text-slate-400">
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-sky-400" />
                604-375-8637
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-sky-400" />
                CONNECT@CRYSTALCLEAR.HELP
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-sky-400 mt-1" />
                Metro Vancouver
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800 mt-12 pt-8 text-center text-slate-500 text-sm">
          <p>&copy; {new Date().getFullYear()} Crystal Clear Home Services LTD. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <Navigation />
      <main>
        <HeroSection />
        <ServicesSection />
        <WhyChooseUsSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
